import { ArrowLeft, Building2, Eye, EyeOff, HeartHandshake, LockKeyhole, ShieldCheck } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Brand } from '../components/Brand'
import { DisplayControls } from '../components/DisplayControls'
import { useAuth } from '../core/auth'
import { useI18n } from '../core/i18n'
import type { UserRole } from '../types'

export function LoginPage({ role }: { role: UserRole }) {
  const { locale } = useI18n()
  const { login, previewLogin, previewEnabled } = useAuth()
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const ko = locale === 'ko'
  const isHospital = role === 'hospital_admin'
  const isSuper = role === 'super_admin'
  const destination = isHospital ? '/hospital-admin' : isSuper ? '/super-admin' : '/member'
  const title = isHospital ? (ko ? '병원 관리자 로그인' : 'Clinic administrator sign in') : isSuper ? (ko ? '최고 관리자 로그인' : 'Super administrator sign in') : (ko ? '내 기록 로그인' : 'Sign in to my records')
  const description = isHospital
    ? (ko ? '최고 관리자가 보낸 초대메일에서 비밀번호 설정을 마친 뒤, 병원 계정 ID 또는 인증된 이메일로 로그인하세요.' : 'After setting a password from the invitation email, sign in with the clinic ID or verified email.')
    : isSuper
      ? (ko ? '최초 개설에서 등록한 최고 관리자 ID 또는 인증된 이메일을 입력하세요.' : 'Use the administrator ID or verified email created during setup.')
      : (ko ? '내 반려동물 기록을 작성하고 병원 공유 링크를 관리합니다.' : 'Create pet records and manage secure clinic sharing links.')
  const Icon = isHospital ? Building2 : isSuper ? ShieldCheck : HeartHandshake

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setError('')
    setLoading(true)
    try {
      await login(identifier.trim(), password, role)
      navigate(destination)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : ko ? '로그인할 수 없습니다.' : 'Unable to sign in.')
    } finally {
      setLoading(false)
    }
  }

  const enterPreview = () => { previewLogin(role); navigate(destination) }

  return (
    <div className="login-page login-redesign">
      <header className="login-header"><Brand /><DisplayControls /></header>
      <main className="login-layout">
        <section className="login-photo" aria-label={ko ? '보호자와 수의사가 기록을 확인하는 모습' : 'Guardian and veterinarian reviewing records'}>
          <img src="/images/vet-handoff-v1.webp" alt="" />
          <div><LockKeyhole /><strong>{ko ? '기록은 승인된 사람에게만 공개됩니다.' : 'Records are visible only to authorized people.'}</strong></div>
        </section>
        <section className="login-form-wrap">
          <div className="login-form-card">
            <Link to={isSuper || isHospital ? '/' : '/'} className="back-link"><ArrowLeft />{ko ? '돌아가기' : 'Back'}</Link>
            <div className="login-heading"><span className="login-icon"><Icon /></span><h1>{title}</h1><p>{description}</p></div>
            <form onSubmit={submit}>
              <label><span>{ko ? '계정 ID 또는 이메일' : 'Account ID or email'}</span><input type="text" autoComplete="username" value={identifier} onChange={(event) => setIdentifier(event.target.value)} placeholder={ko ? '등록된 계정 ID 또는 이메일' : 'Registered account ID or email'} required /><small>{ko ? '대소문자를 포함해 등록된 계정 정보를 입력하세요.' : 'Enter the account information exactly as registered.'}</small></label>
              <label><span>{ko ? '비밀번호' : 'Password'}</span><div className="password-field"><input type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} minLength={10} required /><button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}>{showPassword ? <EyeOff /> : <Eye />}</button></div></label>
              {error && <p className="form-error" role="alert">{error}</p>}
              <button className="primary-button login-submit" type="submit" disabled={loading}><LockKeyhole />{loading ? (ko ? '확인 중…' : 'Signing in…') : (ko ? '로그인' : 'Sign in')}</button>
            </form>
            <div className="login-help"><Link to="/member/forgot-password">{ko ? '비밀번호 재설정' : 'Reset password'}</Link>{!isSuper && !isHospital && <><span aria-hidden="true">/</span><Link to="/member/signup">{ko ? '신규 회원가입' : 'Create account'}</Link></>}</div>
            {isSuper && <p className="setup-guidance">{ko ? '아직 최고 관리자 계정을 만들지 않았다면 운영 담당자에게 전달받은 최초 개설 주소에서 먼저 등록해 주세요.' : 'If no super administrator exists yet, use the private initialization address provided by the operator.'}</p>}
            {isHospital && <p className="setup-guidance">{ko ? '아직 비밀번호를 만든 적이 없다면 로그인할 수 없습니다. 최고 관리자에게 계정 초대메일 재발송을 요청하세요.' : 'If you have never set a password, ask the super administrator to resend the account invitation.'}</p>}
            {previewEnabled && <div className="preview-entry"><strong>{ko ? '로컬 화면 확인' : 'Local preview'}</strong><p>{ko ? '운영 데이터에 연결하지 않고 화면만 확인합니다.' : 'Preview the interface without production data.'}</p><button className="secondary-button" onClick={enterPreview}>{ko ? '미리보기 입장' : 'Open preview'}</button></div>}
          </div>
        </section>
      </main>
    </div>
  )
}
